'use client'

import { useState } from 'react'
import { Copy, Check, Repeat, AlertTriangle, Wallet, OctagonX, ShieldCheck, FileDown } from 'lucide-react'
import {
  TASK_TYPES,
  CADENCES,
  RISK_LEVELS,
  buildLoopKit,
  loopKitToMarkdown,
  type Cadence,
  type Risk,
  type LoopKit,
} from './loopKitData'

export default function LoopBuilderTool() {
  const [taskId, setTaskId] = useState<string>(TASK_TYPES[0].id)
  const [cadence, setCadence] = useState<Cadence>('one-shot')
  const [risk, setRisk] = useState<Risk>('medium')
  const [kit, setKit] = useState<LoopKit | null>(null)
  const [copiedPrompt, setCopiedPrompt] = useState(false)
  const [copiedKit, setCopiedKit] = useState(false)

  const handleBuild = () => {
    const built = buildLoopKit(taskId, cadence, risk)
    setKit(built)

    if (typeof window !== 'undefined' && typeof (window as any).posthog?.capture === 'function') {
      ;(window as any).posthog.capture('loop_builder_used', {
        task_type: taskId,
        cadence,
        risk,
      })
    }
  }

  const handleCopyPrompt = async () => {
    if (!kit) return
    await navigator.clipboard.writeText(kit.loopPrompt)
    setCopiedPrompt(true)
    setTimeout(() => setCopiedPrompt(false), 2000)
  }

  const handleCopyKit = async () => {
    if (!kit) return
    await navigator.clipboard.writeText(loopKitToMarkdown(kit))
    setCopiedKit(true)
    setTimeout(() => setCopiedKit(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
        {/* Task type */}
        <label className="text-sm font-medium text-gray-300 block mb-3">
          1. What should the loop do?
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {TASK_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTaskId(t.id)}
              title={t.description}
              className={`rounded-xl border px-3 py-3 text-left transition-all ${
                taskId === t.id
                  ? 'border-white/40 bg-white/10'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]'
              }`}
            >
              <span className="text-lg block mb-1">{t.icon}</span>
              <span className="text-xs font-medium text-gray-200 leading-snug block">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Cadence */}
        <label className="text-sm font-medium text-gray-300 block mb-3">
          2. How does it run?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {CADENCES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCadence(c.id)}
              className={`rounded-xl border px-4 py-3 text-left transition-all ${
                cadence === c.id
                  ? 'border-white/40 bg-white/10'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]'
              }`}
            >
              <span className="text-sm font-medium text-gray-200 block">{c.label}</span>
              <span className="text-xs text-gray-500 block mt-0.5">{c.description}</span>
            </button>
          ))}
        </div>

        {/* Risk */}
        <label className="text-sm font-medium text-gray-300 block mb-3">
          3. What happens if a bad run ships?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6">
          {RISK_LEVELS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRisk(r.id)}
              className={`rounded-xl border px-4 py-3 text-left transition-all ${
                risk === r.id
                  ? 'border-white/40 bg-white/10'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]'
              }`}
            >
              <span className="text-sm font-medium text-gray-200 block">{r.label} risk</span>
              <span className="text-xs text-gray-500 block mt-0.5">{r.description}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handleBuild}
          className="inline-flex items-center gap-2 bg-white text-black font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-gray-200 transition-colors"
        >
          <Repeat className="w-4 h-4" />
          Build My Loop Kit
        </button>
      </div>

      {/* Result */}
      {kit && (
        <div className="space-y-5">
          {/* Kit header */}
          <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-white mb-1">
                  {kit.task.icon} Loop Kit: {kit.task.label}
                </h3>
                <p className="text-xs text-gray-500">
                  {kit.cadence.label} · {kit.risk.label} risk · {kit.loopType}
                </p>
              </div>
              <button
                onClick={handleCopyKit}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5 shrink-0 self-start"
              >
                {copiedKit ? <Check className="w-3.5 h-3.5 text-green-400" /> : <FileDown className="w-3.5 h-3.5" />}
                {copiedKit ? 'Copied' : 'Copy full kit (Markdown)'}
              </button>
            </div>
          </div>

          {/* Verifier checklist */}
          <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-medium text-gray-300">Verifier checklist</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              The loop is only done when every one of these passes. HARD checks are machine-checkable; JUDGMENT checks need a human (or a separate reviewer agent - never the generator grading itself).
            </p>
            <ul className="space-y-3">
              {kit.task.verifier.map((v) => (
                <li key={v.check} className="border border-white/5 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <span
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${
                        v.grade === 'hard'
                          ? 'bg-green-500/10 text-green-400 border-green-500/20'
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}
                    >
                      {v.grade === 'hard' ? 'HARD' : 'JUDGMENT'}
                    </span>
                    <div>
                      <p className="text-sm text-gray-200 leading-snug">{v.check}</p>
                      <p className="text-xs text-gray-500 mt-1">Prove it: {v.how}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Stopping + budget */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-2 mb-3">
                <OctagonX className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-medium text-gray-300">Stopping condition</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  Every verifier check passes, or
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  <span>
                    <strong className="text-gray-300">{kit.risk.iterationCap} rounds</strong> completed - then summarize what passed, what failed, and why, then stop
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5 shrink-0">&#10003;</span>
                  A tripwire fires (immediate stop + report)
                </li>
              </ul>
              <p className="text-xs text-gray-600 mt-3">
                The bound is what turns &quot;keep iterating&quot; into a loop that can&apos;t spin forever.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-4 h-4 text-blue-400" />
                <h3 className="text-sm font-medium text-gray-300">Budget cap</h3>
              </div>
              <p className="text-sm text-gray-400">
                <strong className="text-gray-200">~${kit.risk.perRunUsd} hard cap per run.</strong>{' '}
                {kit.cadence.id === 'recurring' && (
                  <>
                    <strong className="text-gray-200">~${kit.risk.monthlyUsd}/month</strong> ceiling for the loop.{' '}
                  </>
                )}
                If the next round would exceed it, the agent stops and reports instead of continuing.
              </p>
              <p className="text-xs text-gray-600 mt-3">
                Start tight and raise only with run data. An unattended loop with a weak verifier doesn&apos;t fail loudly - it fails at token prices, all night.
              </p>
            </div>
          </div>

          {/* Tripwires */}
          <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-medium text-gray-300">Doom-loop tripwires</h3>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              If any of these happens, the agent stops immediately and reports - it does not try to recover.
            </p>
            <ul className="space-y-2">
              {[...kit.task.tripwires, 'No measurable progress for 2 consecutive rounds.'].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-gray-400">
                  <span className="text-amber-400 mt-0.5 shrink-0">&#9888;</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Loop prompt */}
          <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-300">Your loop prompt</h3>
              <button
                onClick={handleCopyPrompt}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/5"
              >
                {copiedPrompt ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedPrompt ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="bg-white/5 rounded-xl p-4 text-xs sm:text-sm text-white leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto">
              {kit.loopPrompt}
            </pre>
            <p className="text-xs text-gray-600 mt-3">
              Paste this into Claude Code (works well with <code className="text-gray-500">/goal</code>), Cursor, or any agent harness. Fill in the specifics - paths, thresholds, the exact scope - before running.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
